/**
 * The share text.
 *
 * `buildChallengeShareText`, "Beat me" with the emoji board (docs/SHARING-BUILD.md):
 *
 *   Sparrow No. 42 · Hard
 *   6:42 · Flow ×7 · 2 checks
 *   ⬛⬛⬛⬛⬛⬛⬛⬛
 *   ⬛🟦🟦🟦⬛🟩🟩🟩
 *   …
 *   Faster than 83% today. Beat me?
 *   https://sparrowword.com/c/K7Q2
 *
 * `buildResultShareText` is the same without "Beat me?" ("Share result only"). The board is
 * drawn at most `SHARE_BOARD_MAX_WIDTH` columns wide.
 */
import type { Level } from '@arrow/puzzle-schema';
import { type CellOutcome, emojiBoard } from './outcomes.ts';

export interface ChallengeShareInput {
	/** The `No. n` daily number. `0` or less (practice: not a daily) leaves the number out. */
	number: number;
	/** The level, named in the header: `· Hard`. */
	level: Level;
	elapsedMs: number;
	/** Best Flow this puzzle: `Flow ×n` when n ≥ 2. */
	bestFlow: number;
	/** Checks used: `n checks` when > 0. */
	checks: number;
	/** Reveals used: `n reveals` when > 0. No checks and no reveals reads `perfect`. */
	reveals: number;
	/** The solve per cell (`solveOutcomes`). With `width` ≤ 9, drawn as the emoji board. */
	outcomes?: readonly CellOutcome[];
	/** The board's width, for the emoji board. */
	width?: number;
	/**
	 * Draw the emoji board (default true). False leaves it out (the player's "Picture
	 * board in shares" setting): a screen reader reads it square by square, and the stats
	 * line already says how the solve went.
	 */
	board?: boolean;
	/** % of today's solvers slower (`/stats`), or null while it isn't honest yet. */
	percentile?: number | null;
	/** The last line: the challenge link (or the daily's, for a result share). */
	url: string;
}

/** Boards wider than this get no emoji board in the text (the link's card carries the picture). */
export const SHARE_BOARD_MAX_WIDTH = 8;

const count = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);
const plural = (n: number, one: string): string => `${n} ${one}${n === 1 ? '' : 's'}`;

/** `Sparrow No. 42 · Hard` (`Sparrow · Hard` for practice). */
function header(number: number, level: Level | undefined): string {
	const name = number > 0 ? `Sparrow No. ${Math.floor(number)}` : 'Sparrow';
	return level ? `${name} · ${levelName(level)}` : name;
}

/** `6:42 · Flow ×7 · 2 checks · 1 reveal`, zero parts dropped; no help at all is `perfect`. */
function statsLine(input: ChallengeShareInput): string {
	const parts = [formatDuration(input.elapsedMs)];
	const flow = count(input.bestFlow);
	if (flow >= 2) parts.push(`Flow ×${flow}`);
	const checks = count(input.checks);
	const reveals = count(input.reveals);
	if (checks > 0) parts.push(plural(checks, 'check'));
	if (reveals > 0) parts.push(plural(reveals, 'reveal'));
	if (checks === 0 && reveals === 0) parts.push('perfect');
	return parts.join(' · ');
}

/** The emoji board, or nothing for a big board or outcomes that don't fit the width. */
function boardLines(outcomes: readonly CellOutcome[] | undefined, width: number | undefined): string[] {
	if (!outcomes || outcomes.length === 0 || width === undefined) return [];
	if (!Number.isInteger(width) || width <= 0 || width > SHARE_BOARD_MAX_WIDTH) return [];
	if (outcomes.length % width !== 0) return [];
	return emojiBoard(outcomes, width);
}

/** `Faster than 83% today.` for an honest percentile of at least 1, else null. */
function percentileText(percentile: number | null | undefined): string | null {
	if (typeof percentile !== 'number' || !Number.isFinite(percentile)) return null;
	const p = Math.floor(Math.min(100, percentile));
	return p >= 1 ? `Faster than ${p}% today.` : null;
}

function shareText(input: ChallengeShareInput, beatMe: boolean): string {
	const picture = input.board === false ? [] : boardLines(input.outcomes, input.width);
	const lines = [header(input.number, input.level), statsLine(input), ...picture];
	const brag = percentileText(input.percentile);
	const close = beatMe ? (brag ? `${brag} Beat me?` : 'Beat me?') : brag;
	if (close) lines.push(close);
	if (input.url) lines.push(input.url);
	return lines.join('\n');
}

/**
 * The share: header, stats, the emoji board (≤ 9 columns), "Beat me?" (after
 * "Faster than n% today." when there's a percentile), then the challenge link. Glyphs
 * are only ⬛🟦🟩🟨🟥, digits, "·" and "×": never letters of the solve, clues or answers.
 */
export function buildChallengeShareText(input: ChallengeShareInput): string {
	return shareText(input, true);
}

/** "Share result only": `buildChallengeShareText` without "Beat me?" (pass the daily's link as `url`). */
export function buildResultShareText(input: ChallengeShareInput): string {
	return shareText(input, false);
}

/**
 * The solve in one line of words, for assistive tech (the poster's summary):
 * `Solved in 6:42, best Flow 7, 2 checks.` The same facts as the share's stats line, with
 * no symbols to be read aloud ("×", "·"); no checks and no reveals reads `a perfect solve`.
 */
export function shareSummary(input: Pick<ChallengeShareInput, 'elapsedMs' | 'bestFlow' | 'checks' | 'reveals'>): string {
	const parts = [`Solved in ${formatDuration(input.elapsedMs)}`];
	const flow = count(input.bestFlow);
	if (flow >= 2) parts.push(`best Flow ${flow}`);
	const checks = count(input.checks);
	const reveals = count(input.reveals);
	if (checks > 0) parts.push(plural(checks, 'check'));
	if (reveals > 0) parts.push(plural(reveals, 'reveal'));
	if (checks === 0 && reveals === 0) parts.push('a perfect solve');
	return `${parts.join(', ')}.`;
}

// ---------------------------------------------------------------------------
// Deprecated: the ledger
//
//   Sparrow No. 42 · Hard
//   ⏱ 6:42 · 🔥 8 · 💡 0
//   ↓→
//   →↓→↓→↓→  ×7
//   https://sparrowword.com/daily/42?s=1&l=hard
// ---------------------------------------------------------------------------


/** `m:ss`, or `h:mm:ss` from an hour. Negative / non-finite → `0:00`. Seconds are floored. */
export function formatDuration(ms: number): string {
	const total = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	const ss = String(s).padStart(2, '0');
	return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// Every arrow is one UTF-16 code unit, so string length counts arrows.

const LEVEL_NAMES: Record<Level, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

/** `Easy` / `Medium` / `Hard`: the level as the share text and headers print it. */
export function levelName(level: Level): string {
	return LEVEL_NAMES[level] ?? level;
}

